import { call, put, select } from 'redux-saga/effects';
import axios from 'axios';
import * as actions from './actions';
import { getEnvVar } from 'helpers';

const getContentServiceApiRoot = () =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/content-service/content/adm/v1`;

const getCurrentTenantId = store =>
  store.user?.selectedTenant?.tenantId ||
  window.localStorage.getItem('GCM-tenantId');

const getCurrentPropertyId = store =>
  store.user?.selectedBusinessContext?.businessContextId ||
  window.localStorage.getItem('GCM-businessContextId') ||
  'Default';

const getCurrentFilters = store => store.uploads?.filters || {};

const getAdmAzureApiRoot = (tenantId, propertyId) =>
  `${getContentServiceApiRoot()}/tenants/${tenantId}/properties/${propertyId}/azure`;

const normalizeCustomTags = customTags => {
  if (Array.isArray(customTags)) {
    return customTags.filter(Boolean);
  }

  if (typeof customTags === 'string') {
    return customTags
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
  }

  return [];
};

const getAssetName = asset =>
  String(asset?.fileName || asset?.name || '').trim();

const getAssetFileSize = asset =>
  Number(asset?.fileSize || asset?.size || 0) || 0;

const getComparableValue = (asset, sortBy) => {
  switch (sortBy) {
    case 'fileSize':
      return getAssetFileSize(asset);
    case 'updatedOn':
      return new Date(
        asset?.updatedOn ||
          asset?.lastModifiedDate ||
          asset?.modifiedOn ||
          asset?.createdOn ||
          asset?.updateTime ||
          asset?.createTime ||
          0
      ).getTime();
    // case 'usageTypeTag':
    //   return String(asset?.usageTypeTag || asset?.standardUsageTypeTag || '').toLowerCase();
    case 'fileName':
    default:
      return getAssetName(asset).toLowerCase();
  }
};

const sortAssets = (assets, sortBy, sortOrder) => {
  const order = sortOrder === 'DESC' ? -1 : 1;
  return [...assets].sort((left, right) => {
    const leftValue = getComparableValue(left, sortBy);
    const rightValue = getComparableValue(right, sortBy);

    if (leftValue < rightValue) {
      return -1 * order;
    }

    if (leftValue > rightValue) {
      return 1 * order;
    }

    return 0;
  });
};

const filterAssetsBySearch = (assets, searchInput) => {
  const normalizedSearch = String(searchInput || '').trim().toLowerCase();

  if (!normalizedSearch) {
    return assets;
  }

  return assets.filter(asset => {
    const candidates = [
      asset?.fileName,
      asset?.name,
      asset?.cdnPublicUrl,
      asset?.publicUrl,
      asset?.usageTypeTag,
      asset?.standardUsageTypeTag,
    ];

    return candidates.some(value =>
      String(value || '')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  });
};

const buildSearchQuery = filters => {
  const searchParams = new URLSearchParams();
  if (filters?.usageTypeTag) {
    searchParams.append('usageTypeTag', filters.usageTypeTag);
  }

  const defaultTag = getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm';
  if (defaultTag) {
    searchParams.append('customTag', defaultTag);
  }

  if (filters?.sizeGte !== '' && filters?.sizeGte !== null && filters?.sizeGte !== undefined) {
    searchParams.append('sizeGte', String(filters.sizeGte));
  }

  if (filters?.sizeLte !== '' && filters?.sizeLte !== null && filters?.sizeLte !== undefined) {
    searchParams.append('sizeLte', String(filters.sizeLte));
  }

  return searchParams.toString();
};

const buildUploadQuery = payload => {
  const searchParams = new URLSearchParams();

  if (payload?.usageTypeTag) {
    searchParams.append('usageTypeTag', payload.usageTypeTag);
  }

  normalizeCustomTags(payload?.customTags).forEach(tag => {
    searchParams.append('customTag', tag);
  });

  searchParams.append('updateBlob', payload?.updateBlob ? 'true' : 'false');

  return searchParams.toString();
};

export const getUploadsAsync = async (tenantId, propertyId, filters) => {
  const apiRoot = getAdmAzureApiRoot(tenantId, propertyId);
  const query = buildSearchQuery(filters);

  try {
    const response = await axios.get(`${apiRoot}/search${query ? `?${query}` : ''}`);
    return response.data;
  } catch (error) {
    if (!query) {
      const fallbackResponse = await axios.get(apiRoot);
      return fallbackResponse.data;
    }

    throw error;
  }
};

export const getUsageTypeTagsAsync = (tenantId, propertyId) =>
  axios
    .get(`${getAdmAzureApiRoot(tenantId, propertyId)}/usageTypeTags`)
    .then(response => response.data);

export const uploadAssetAsync = (tenantId, propertyId, payload) => {
  const apiRoot = getAdmAzureApiRoot(tenantId, propertyId);
  const query = buildUploadQuery(payload);
  const formData = new FormData();

  if (!payload.file) {
    throw new Error('File is required for upload/update');
  }

  formData.append(
    'file',
    payload.file,
    payload.fileNameOverride || payload.file?.name
  );

  normalizeCustomTags(payload?.customTags).forEach(tag => {
    formData.append('customTags', tag);
  });

  if (payload?.usageTypeTag) {
    formData.append('standardUsageTypeTag', payload.usageTypeTag);
  }

  return axios
    .post(`${apiRoot}${query ? `?${query}` : ''}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => response.data)
    .catch(error => {
        throw error; // Let the saga handle the error and dispatch the appropriate action
    });
};

export const deleteUploadAsync = (tenantId, propertyId, uploadId) =>
  axios
    .delete(`${getAdmAzureApiRoot(tenantId, propertyId)}/${uploadId}`)
    .then(response => response.data);

export function* getUploadsIfNeeded(action) {
  try {
    const tenantId = yield select(getCurrentTenantId);
    const propertyId = yield select(getCurrentPropertyId);
    const currentFilters = yield select(getCurrentFilters);
    const filters = {
      ...currentFilters,
      ...(action.payload?.filters || {})
    };

    if (!tenantId || !propertyId) {
      throw new Error('Tenant or property context is unavailable');
    }

    yield put(actions.gettingUploads());

    const data = yield call(getUploadsAsync, tenantId, propertyId, filters);
    const asArray = Array.isArray(data) ? data : [];
    const filtered = filterAssetsBySearch(asArray, filters.searchInput);
    const sorted = sortAssets(filtered, filters.sortBy || 'updatedOn', filters.sortOrder || 'DESC');

    yield put(
      actions.gettingUploadsSucceeded({
        data: sorted,
        totalCount: sorted.length
      })
    );
  } catch (error) {
    yield put(
      actions.gettingUploadsFailed(
        error.response?.data?.message || error.message || 'Failed to fetch uploads'
      )
    );
  }
}

export function* getUsageTypeTagsIfNeeded() {
  try {
    const tenantId = yield select(getCurrentTenantId);
    const propertyId = yield select(getCurrentPropertyId);

    if (!tenantId || !propertyId) {
      throw new Error('Tenant or property context is unavailable');
    }

    yield put(actions.gettingUsageTypeTags());
    const data = yield call(getUsageTypeTagsAsync, tenantId, propertyId);
    yield put(actions.gettingUsageTypeTagsSucceeded(Array.isArray(data) ? data : []));
  } catch (error) {
    yield put(
      actions.gettingUsageTypeTagsFailed(
        error.response?.data?.message || error.message || 'Failed to fetch usage type tags'
      )
    );
  }
}

export function* createUploadIfNeeded(action) {
  try {
    const tenantId = yield select(getCurrentTenantId);
    const propertyId = yield select(getCurrentPropertyId);

    if (!tenantId || !propertyId) {
      throw new Error('Tenant or property context is unavailable');
    }

    yield put(actions.creatingUpload());
    yield call(uploadAssetAsync, tenantId, propertyId, {
      ...action.payload,
      updateBlob: false
    });
    yield put(actions.creatingUploadSucceeded());
    yield call(getUploadsIfNeeded, { payload: {} });
  } catch (error) {
    if (!error.response && error.message === 'Network Error') {
      yield put(
        actions.creatingUploadFailed(
          'Image upload failed: Network Error. Please try again.'
        )
      );
    } else if (error.response && error.response.status === 409) {
      yield put(
        actions.creatingUploadFailed(
          'Image upload failed: A file with the same name already exists. Please rename the file and try again.'
        )
      );
    } else if (error.response && error.response.status === 400) {
      yield put(
        actions.creatingUploadFailed(
          'Image upload failed: Invalid file format or size. Please check the file and try again.'
        )
      );
    } else {
      yield put(
        actions.creatingUploadFailed(
          error.response?.data?.message || error.message || 'Image upload failed: Unknown error occurred.'
        )
      );
    }
  }
}

export function* updateUploadIfNeeded(action) {
  try {
    const tenantId = yield select(getCurrentTenantId);
    const propertyId = yield select(getCurrentPropertyId);
    const fileNameOverride =
      String(action.payload?.fileNameOverride || '').trim() ||
      getAssetName(action.payload?.asset);
    const replacementFile = action.payload?.file;

    if (!tenantId || !propertyId) {
      throw new Error('Tenant or property context is unavailable');
    }

    if (!fileNameOverride) {
      throw new Error('Asset name is unavailable for update');
    }

    if (!replacementFile) {
      throw new Error('Replacement file is required for update');
    }

    yield put(actions.updatingUpload());
    yield call(uploadAssetAsync, tenantId, propertyId, {
      ...action.payload,
      fileNameOverride,
      updateBlob: true
    });
    yield put(actions.updatingUploadSucceeded());
    yield call(getUploadsIfNeeded, { payload: {} });
  } catch (error) {
    yield put(
      actions.updatingUploadFailed(
        error.response?.data?.message || error.message || 'Failed to update asset'
      )
    );
  }
}

export function* deleteUploadIfNeeded(action) {
  try {
    const tenantId = yield select(getCurrentTenantId);
    const propertyId = yield select(getCurrentPropertyId);
    const uploadId = action.payload?.id;

    if (!tenantId || !propertyId) {
      throw new Error('Tenant or property context is unavailable');
    }

    if (!uploadId) {
      throw new Error('Upload ID is required');
    }

    yield put(actions.deletingUpload());
    yield call(deleteUploadAsync, tenantId, propertyId, uploadId);
    yield put(actions.deletingUploadSucceeded());
    yield call(getUploadsIfNeeded, { payload: {} });
  } catch (error) {
    yield put(
      actions.deletingUploadFailed(
        error.response?.data?.message || error.message || 'Failed to delete asset'
      )
    );
  }
}